import React, {useEffect, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faKey,
  faRightFromBracket,
  faSpinner,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import {useUserAuth, useUserProfile, useUserUploads} from "../store/user";
import LearnerShell from "../components/learner/LearnerShell";
import TabStrip from "../components/learner/TabStrip";
import Toast from "../components/Toast";
import {Avatar, AvatarFallback, AvatarImage} from "../components/ui/avatar";
import {displayName, getLearnerAvatar, imageUrl, initialsOf, saveLearnerAvatar, shortDate} from "../lib/format";

const TABS = [
  {id: "profile", label: "Profile"},
  {id: "security", label: "Security"},
  {id: "account", label: "Account"},
];

const FIELD =
  "w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm text-slate-800 outline-none transition-colors focus:border-blue-300";

/**
 * /account/settings — learner profile & security.
 *
 *   GET   /api/learners/me
 *   PATCH /api/learners/me              { name, phone, avatar }
 *   POST  /api/auth/change-password     { currentPassword, newPassword }
 *   POST  /api/auth/resend-verification { email }
 *   POST  /api/auth/logout
 */
const Profile = () => {
  const {
    userProfile: profile,
    userProfileLoading,
    userProfileError,
    fetchUserProfile,
    updateUserProfile,
  } = useUserProfile();
  const {userChangePassword, userResendVerification, userLogout} = useUserAuth();
  const {uploadUserImage, userImageUploadLoading} = useUserUploads();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = TABS.find((entry) => entry.id === searchParams.get("tab"))?.id || "profile";
  const [tab, setTab] = useState(initialTab);
  const [form, setForm] = useState({name: "", phone: "", avatar: ""});
  const [avatarPreview, setAvatarPreview] = useState("");
  const [showAvatarPreview, setShowAvatarPreview] = useState(false);
  const [formError, setFormError] = useState("");
  const [password, setPassword] = useState({currentPassword: "", newPassword: "", confirmPassword: ""});
  const [passwordError, setPasswordError] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [isSuccess, setIsSuccess] = useState(true);

  const showToast = (message, success = true) => {
    setToast(message);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 5000);
  };

  // GET /api/learners/me
  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  useEffect(() => {
    if (!profile) return;
    setForm({
      name: profile.name || "",
      phone: profile.phone || "",
      avatar: getLearnerAvatar(profile),
    });
  }, [profile]);

  useEffect(() => () => {
    if (avatarPreview.startsWith("blob:")) URL.revokeObjectURL(avatarPreview);
  }, [avatarPreview]);

  const uploadAvatar = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setFormError("Choose an image file.");
      return;
    }
    setFormError("");
    setShowAvatarPreview(true);
    setAvatarPreview(URL.createObjectURL(file));
    const result = await uploadUserImage(file, "learner-avatars");
    if (!result.success) {
      setAvatarPreview("");
      setShowAvatarPreview(false);
      setFormError(result.error || "Image upload failed.");
      return;
    }
    const uploaded = result.upload || {};
    const url = uploaded.secure_url || uploaded.secureUrl || uploaded.url || uploaded.imageUrl || uploaded.image || uploaded.fileUrl;
    if (!url) {
      setAvatarPreview("");
      setShowAvatarPreview(false);
      setFormError("The upload succeeded, but the server did not return an image URL.");
      return;
    }
    setForm((previous) => ({...previous, avatar: url}));
    setAvatarPreview("");
  };

  const selectTab = (next) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    if (next === "profile") params.delete("tab");
    else params.set("tab", next);
    setSearchParams(params, {replace: true});
  };

  // PATCH /api/learners/me — { name, phone, avatar }
  const saveProfile = async (event) => {
    event.preventDefault();
    setFormError("");
    const payload = {};
    if (form.name.trim()) payload.name = form.name.trim();
    if (form.phone.trim()) payload.phone = form.phone.trim();
    if (form.avatar.trim()) payload.avatar = form.avatar.trim();
    if (Object.keys(payload).length === 0) {
      setFormError("Change at least one field before saving.");
      return;
    }
    setBusy(true);
    const result = await updateUserProfile(payload);
    setBusy(false);
    if (!result.success) {
      setFormError(result.error || "Failed to update profile");
      return;
    }
    try {
      const savedAvatar = form.avatar.trim() || result.profile?.avatar || "";
      saveLearnerAvatar(savedAvatar);
      if (savedAvatar) setForm((previous) => ({...previous, avatar: savedAvatar}));
    } catch { /* server profile is already saved */ }
    setShowAvatarPreview(false);
    setAvatarPreview("");
    showToast("Profile updated");
  };

  // POST /api/auth/change-password — { currentPassword, newPassword }
  const changePassword = async (event) => {
    event.preventDefault();
    setPasswordError("");
    if (password.newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }
    if (!/[A-Z]/.test(password.newPassword)) {
      setPasswordError("New password must contain at least one uppercase letter.");
      return;
    }
    if (password.newPassword !== password.confirmPassword) {
      setPasswordError("New password and confirmation do not match.");
      return;
    }
    setBusy(true);
    const result = await userChangePassword({
      currentPassword: password.currentPassword,
      newPassword: password.newPassword,
    });
    setBusy(false);
    if (!result.success) {
      setPasswordError(result.error || "Failed to change password");
      return;
    }
    setPassword({currentPassword: "", newPassword: "", confirmPassword: ""});
    showToast("Password changed successfully");
  };

  // POST /api/auth/resend-verification — { email }
  const resendVerification = async () => {
    if (!profile?.email) return;
    const result = await userResendVerification({email: profile.email});
    showToast(
      result.success ? "Verification email sent" : result.error || "Could not send the email",
      !!result.success
    );
  };

  // POST /api/auth/logout
  const logout = async () => {
    await userLogout();
    window.location.href = "/login";
  };

  const rowButton =
    "inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100";
  const primaryButton =
    "inline-flex items-center justify-center gap-2 rounded-md bg-blue-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60";

  return (
    <LearnerShell>
      <header className="mb-5">
        <h1 className="m-0 text-2xl font-bold text-slate-900">Account settings</h1>
        <p className="m-0 mt-1 text-sm text-slate-500">Manage your learner profile, password and account details.</p>
      </header>
      {toast ? <Toast message={toast} onClose={() => setToast(null)} isSuccess={isSuccess} /> : null}

      {/* Identity header */}
      <section className="mb-5 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <Avatar className="h-16 w-16 shrink-0 border border-slate-200">
            <AvatarImage src={avatarPreview || imageUrl(form.avatar || getLearnerAvatar(profile)) || "/images/default_profile.png"} alt={displayName(profile)} />
            <AvatarFallback className="bg-indigo-50 text-lg font-semibold text-indigo-700">
              {initialsOf(profile)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <h2 className="m-0 truncate text-lg font-bold text-slate-900">{displayName(profile)}</h2>
            <p className="m-0 truncate text-sm text-slate-500">{profile?.email || "—"}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {profile?.emailVerified ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                  <FontAwesomeIcon icon={faCircleCheck} /> Verified
                </span>
              ) : (
                <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                  Email not verified
                </span>
              )}
              {profile?.status ? (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                  {profile.status}
                </span>
              ) : null}
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                Member since {shortDate(profile?.createdAt) || "—"}
              </span>
            </div>
          </div>
        </div>
        <button type="button" className={rowButton} onClick={logout}>
          <FontAwesomeIcon icon={faRightFromBracket} />
          Log out
        </button>
      </section>

      {userProfileError ? (
        <p className="m-0 mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">{userProfileError}</p>
      ) : null}

      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <TabStrip tabs={TABS} active={tab} onChange={selectTab} />
        <div className="p-4 sm:p-6">
          {userProfileLoading && !profile ? (
            <div className="h-64 animate-pulse rounded-xl border border-slate-200 bg-white" />
          ) : null}

          {/* Profile */}
          {tab === "profile" ? (
            <form onSubmit={saveProfile} className="max-w-2xl">
              <h3 className="m-0 flex items-center gap-2 text-base font-semibold text-slate-800">
                <FontAwesomeIcon icon={faUser} className="text-slate-400" />
                Profile details
              </h3>
              {formError ? (
                <p className="mt-4 rounded-lg bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700">{formError}</p>
              ) : null}

              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Full name
                  </span>
                  <input
                    className={FIELD}
                    value={form.name}
                    minLength={2}
                    placeholder="Ada Lovelace"
                    onChange={(event) => setForm((prev) => ({...prev, name: event.target.value}))}
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Phone
                  </span>
                  <input
                    className={FIELD}
                    value={form.phone}
                    placeholder="+2348012345678"
                    onChange={(event) => setForm((prev) => ({...prev, phone: event.target.value}))}
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Upload profile photo
                  </span>
                  <input
                    className={FIELD}
                    type="file"
                    accept="image/*"
                    onChange={uploadAvatar}
                    disabled={userImageUploadLoading}
                  />
                  <span className="mt-1 block text-xs text-slate-500">
                    {userImageUploadLoading ? "Uploading image…" : "Choose an image up to 10 MB. Then save your changes."}
                  </span>
                  {showAvatarPreview ? (
                    <div className="mt-3 flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <img
                        src={avatarPreview || imageUrl(form.avatar)}
                        alt="Selected profile preview"
                        className="h-16 w-16 rounded-full border border-slate-200 object-cover"
                      />
                      <span className="text-sm font-medium text-slate-700">Image preview</span>
                    </div>
                  ) : null}
                </label>
                <label className="block sm:col-span-2">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Email (read-only)
                  </span>
                  <input className={`${FIELD} bg-slate-50 text-slate-500`} value={profile?.email || ""} readOnly />
                </label>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button type="submit" className={primaryButton} disabled={busy}>
                  {busy ? <FontAwesomeIcon icon={faSpinner} spin /> : null}
                  Save changes
                </button>
                <button
                  type="button"
                  className={rowButton}
                  onClick={() =>
                    {
                      setForm({
                        name: profile?.name || "",
                        phone: profile?.phone || "",
                        avatar: getLearnerAvatar(profile),
                      });
                      setShowAvatarPreview(false);
                      setAvatarPreview("");
                    }
                  }
                >
                  Reset
                </button>
              </div>
            </form>
          ) : null}

          {/* Security */}
          {tab === "security" ? (
            <div className="grid gap-6 lg:grid-cols-2">
              <form onSubmit={changePassword} className="max-w-xl">
                <h3 className="m-0 flex items-center gap-2 text-base font-semibold text-slate-800">
                  <FontAwesomeIcon icon={faKey} className="text-slate-400" />
                  Change password
                </h3>
                <p className="m-0 mt-1 text-sm text-slate-500">
                  POST /api/auth/change-password — at least 8 characters with one uppercase letter.
                </p>

                {passwordError ? (
                  <p className="mt-4 rounded-lg bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700">
                    {passwordError}
                  </p>
                ) : null}

                <div className="mt-5 space-y-4">
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Current password
                    </span>
                    <input
                      type="password"
                      autoComplete="current-password"
                      className={FIELD}
                      value={password.currentPassword}
                      onChange={(event) =>
                        setPassword((prev) => ({...prev, currentPassword: event.target.value}))
                      }
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      New password
                    </span>
                    <input
                      type="password"
                      autoComplete="new-password"
                      className={FIELD}
                      value={password.newPassword}
                      onChange={(event) => setPassword((prev) => ({...prev, newPassword: event.target.value}))}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Confirm new password
                    </span>
                    <input
                      type="password"
                      autoComplete="new-password"
                      className={FIELD}
                      value={password.confirmPassword}
                      onChange={(event) =>
                        setPassword((prev) => ({...prev, confirmPassword: event.target.value}))
                      }
                    />
                  </label>
                </div>

                <button type="submit" className={`${primaryButton} mt-5`} disabled={busy}>
                  {busy ? <FontAwesomeIcon icon={faSpinner} spin /> : null}
                  Update password
                </button>
              </form>

              <section className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                <h3 className="m-0 text-base font-semibold text-slate-800">Email verification</h3>
                <p className="m-0 mt-1 text-sm text-slate-600">
                  {profile?.emailVerified
                    ? `Verified${profile?.emailVerifiedAt ? ` on ${shortDate(profile.emailVerifiedAt)}` : ""}.`
                    : "Your email is not verified yet. Verify it to unlock every learner feature."}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {!profile?.emailVerified ? (
                    <button type="button" className={primaryButton} onClick={resendVerification}>
                      Resend verification email
                    </button>
                  ) : null}
                  <button type="button" className={rowButton} onClick={() => navigate("/verify-email")}>
                    Open verification page
                  </button>
                </div>
              </section>
            </div>
          ) : null}

          {/* Account */}
          {tab === "account" ? (
            <div className="grid gap-6 lg:grid-cols-2">
              <section className="rounded-xl border border-slate-200 bg-white">
                <h3 className="m-0 border-b border-slate-100 p-4 text-base font-semibold text-slate-800">
                  Account information
                </h3>
                <dl className="m-0 divide-y divide-slate-100 text-sm">
                  {[
                    ["Learner ID", profile?.id || "—"],
                    ["Email", profile?.email || "—"],
                    ["Status", profile?.status || "—"],
                    ["Email verified", profile?.emailVerified ? "Yes" : "No"],
                    ["Verified at", shortDate(profile?.emailVerifiedAt) || "—"],
                    ["Last login", shortDate(profile?.lastLoginAt) || "—"],
                    ["Joined", shortDate(profile?.createdAt) || "—"],
                    ["Updated", shortDate(profile?.updatedAt) || "—"],
                  ].map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between gap-4 px-4 py-2.5">
                      <dt className="text-slate-500">{label}</dt>
                      <dd className="m-0 truncate font-medium text-slate-800">{value}</dd>
                    </div>
                  ))}
                </dl>
              </section>

            </div>
          ) : null}
        </div>
      </section>
    </LearnerShell>
  );
};

export default Profile;
